/**
 * ContextTiles - Enhanced time, place, lunar, and solar display tiles
 * 
 * LAYOUT (v2.16):
 * ┌─────────────────────────────────────┐
 * │ [Time Tile]  │  [Place Tile]        │  ← Side by side, small
 * ├─────────────────────────────────────┤
 * │ [Now] │ [Current Location]          │  ← Quick action buttons
 * ├─────────────────────────────────────┤
 * │        [MOON TILE - 4:1]            │  ← Large with lunar visual
 * ├─────────────────────────────────────┤
 * │        [SUN TILE - 4:1]             │  ← Large with solar weather
 * └─────────────────────────────────────┘
 * 
 * META ADHERENCE:
 * - Uses REAL_TIME_DISPLAY and REAL_PLACE_DISPLAY from styleCard.js
 * - High contrast design for visibility
 * - Real-time data integration
 * - THEME-AWARE: Supports light and dark themes via useTheme
 */
import React, { useMemo } from 'react';
import { Clock, MapPin, Sun, Moon, Zap, Navigation, RefreshCw, Layers } from 'lucide-react';
import { REAL_TIME_DISPLAY, REAL_PLACE_DISPLAY, getCihPhaseColor, getLunarCihPhase, TIMING_UTILITIES, THEME_VARIANTS } from '../utils/styleCard';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsive } from '../hooks/useResponsive';

// =============================================================================
// LUNAR PHASE VISUAL - SVG rendering of moon with shadow
// =============================================================================
const LunarPhaseVisual = ({ illumination = 50, phaseAngle = 180, size = 60 }) => {
  // phaseAngle: 0 = New Moon, 90 = First Quarter, 180 = Full Moon, 270 = Last Quarter
  const radius = size / 2 - 2;
  const center = size / 2;
  
  // Determine if waxing (right side lit) or waning (left side lit)
  const isWaxing = phaseAngle < 180;
  
  // Calculate how much of the moon is illuminated (0 to 1)
  const illumFraction = Math.min(100, Math.max(0, illumination)) / 100;
  
  // Calculate the terminator curve based on illumination
  // illumFraction 0 = new moon (terminator at edge)
  // illumFraction 0.5 = quarter moon (terminator at center)  
  // illumFraction 1 = full moon (no shadow)
  
  // The terminator position: how far the "shadow edge" is from center
  // Maps illumination to the x-offset of the terminator ellipse
  const terminatorOffset = (1 - illumFraction * 2) * radius; // +radius to -radius
  
  // Create the illuminated portion path using a crescent shape
  const createMoonPath = () => {
    if (illumination <= 2) {
      // New moon - no illumination
      return null;
    }
    
    if (illumination >= 98) {
      // Full moon - full circle
      return `M ${center} ${center - radius} 
              A ${radius} ${radius} 0 1 1 ${center} ${center + radius} 
              A ${radius} ${radius} 0 1 1 ${center} ${center - radius}`;
    }
    
    // For crescent/gibbous phases, draw a crescent shape
    // The outer arc is always the full radius circle
    // The inner arc (terminator) is an ellipse that creates the shadow edge
    
    const absOffset = Math.abs(terminatorOffset);
    const isGibbous = illumFraction > 0.5;
    
    // SVG Arc: A rx ry x-rotation large-arc sweep-flag x y
    // sweep-flag: 0=counter-clockwise, 1=clockwise
    // 
    // WAXING (right lit): Draw outer arc clockwise (right side), inner arc back
    // WANING (left lit): Draw outer arc counter-clockwise (left side), inner arc back
    
    if (isWaxing) {
      // Waxing: RIGHT side illuminated
      // Start at top, arc clockwise to bottom (covers right side), then inner arc back to top
      if (isGibbous) {
        // Gibbous: terminator curves left (into shadow) - inner arc sweeps opposite
        return `M ${center} ${center - radius}
                A ${radius} ${radius} 0 0 1 ${center} ${center + radius}
                A ${absOffset} ${radius} 0 0 1 ${center} ${center - radius}`;
      } else {
        // Crescent: terminator curves right (into lit area) - inner arc same sweep
        return `M ${center} ${center - radius}
                A ${radius} ${radius} 0 0 1 ${center} ${center + radius}
                A ${absOffset} ${radius} 0 0 0 ${center} ${center - radius}`;
      }
    } else {
      // Waning: LEFT side illuminated
      // Start at top, arc counter-clockwise to bottom (covers left side), then inner arc back to top
      if (isGibbous) {
        // Gibbous: terminator curves right (into shadow) - inner arc sweeps opposite
        return `M ${center} ${center - radius}
                A ${radius} ${radius} 0 0 0 ${center} ${center + radius}
                A ${absOffset} ${radius} 0 0 0 ${center} ${center - radius}`;
      } else {
        // Crescent: terminator curves left (into lit area) - inner arc same sweep
        return `M ${center} ${center - radius}
                A ${radius} ${radius} 0 0 0 ${center} ${center + radius}
                A ${absOffset} ${radius} 0 0 1 ${center} ${center - radius}`;
      }
    }
  };
  
  const moonPath = createMoonPath();
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="drop-shadow-lg">
      {/* Dark side (shadow - always visible as base) */}
      <circle 
        cx={center} 
        cy={center} 
        r={radius} 
        fill="#1a1a2e"
        stroke="#3d3d5c"
        strokeWidth="1"
      />
      
      {/* Surface texture (craters on dark side) */}
      <circle cx={center - radius * 0.3} cy={center - radius * 0.2} r={radius * 0.15} fill="#252540" opacity="0.5" />
      <circle cx={center + radius * 0.2} cy={center + radius * 0.3} r={radius * 0.1} fill="#252540" opacity="0.4" />
      <circle cx={center - radius * 0.1} cy={center + radius * 0.4} r={radius * 0.08} fill="#252540" opacity="0.3" />
      
      {/* Illuminated portion (bright side) */}
      {moonPath && (
        <path 
          d={moonPath}
          fill="url(#moonGradient)"
        />
      )}
      
      {/* Gradient definitions */}
      <defs>
        <radialGradient id="moonGradient" cx="40%" cy="40%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="50%" stopColor="#f0f0e8" />
          <stop offset="100%" stopColor="#d8d8c8" />
        </radialGradient>
      </defs>
      
      {/* Subtle glow for illuminated portion */}
      {illumination > 10 && (
        <circle 
          cx={center} 
          cy={center} 
          r={radius + 2} 
          fill="none"
          stroke="rgba(255, 255, 240, 0.15)"
          strokeWidth="3"
          style={{ filter: 'blur(2px)' }}
        />
      )}
    </svg>
  );
};

// =============================================================================
// SOLAR WEATHER VISUAL - KP Index gauge
// =============================================================================
const SolarWeatherVisual = ({ kpIndex = 2, activityLevel = 'moderate', size = 60 }) => {
  const radius = size / 2 - 4;
  const center = size / 2;
  
  // KP index ranges from 0-9
  // 0-3: Quiet, 4-5: Active, 6-7: Storm, 8-9: Severe Storm
  const normalizedKp = Math.min(9, Math.max(0, kpIndex));
  const angle = (normalizedKp / 9) * 270 - 135; // -135 to +135 degrees
  
  // Color based on KP level
  const getKpColor = (kp) => {
    if (kp <= 3) return '#4ade80'; // Green - quiet
    if (kp <= 5) return '#fbbf24'; // Yellow - active
    if (kp <= 7) return '#f97316'; // Orange - storm
    return '#ef4444'; // Red - severe
  };
  
  const kpColor = getKpColor(normalizedKp);
  
  // Create arc for the gauge background
  const createArc = (startAngle, endAngle, r) => {
    // Guard against undefined values
    if (r === undefined || isNaN(r) || startAngle === undefined || endAngle === undefined) {
      return 'M 0,0';
    }
    const start = polarToCartesian(center, center, r, endAngle);
    const end = polarToCartesian(center, center, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };
  
  const polarToCartesian = (cx, cy, r, angleDegrees) => {
    // Guard against undefined values
    if (cx === undefined || cy === undefined || r === undefined || angleDegrees === undefined) {
      return { x: 0, y: 0 };
    }
    const angleRadians = (angleDegrees - 90) * Math.PI / 180;
    return {
      x: cx + r * Math.cos(angleRadians),
      y: cy + r * Math.sin(angleRadians)
    };
  };
  
  // Needle position
  const needleEnd = polarToCartesian(center, center, radius - 8, angle);
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="drop-shadow-lg">
      {/* Background circle */}
      <circle cx={center} cy={center} r={radius} fill="#0a0a1a" stroke="#2a2a4a" strokeWidth="1" />
      
      {/* Gauge arc background */}
      <path 
        d={createArc(-135, 135, radius - 4)} 
        fill="none" 
        stroke="#1a1a3a" 
        strokeWidth="6"
        strokeLinecap="round"
      />
      
      {/* Colored segments */}
      <path d={createArc(-135, -45, radius - 4)} fill="none" stroke="#4ade80" strokeWidth="6" strokeLinecap="round" opacity="0.3" />
      <path d={createArc(-45, 15, radius - 4)} fill="none" stroke="#fbbf24" strokeWidth="6" strokeLinecap="round" opacity="0.3" />
      <path d={createArc(15, 75, radius - 4)} fill="none" stroke="#f97316" strokeWidth="6" strokeLinecap="round" opacity="0.3" />
      <path d={createArc(75, 135, radius - 4)} fill="none" stroke="#ef4444" strokeWidth="6" strokeLinecap="round" opacity="0.3" />
      
      {/* Active arc up to current KP */}
      <path 
        d={createArc(-135, angle, radius - 4)} 
        fill="none" 
        stroke={kpColor} 
        strokeWidth="6"
        strokeLinecap="round"
      />
      
      {/* Center hub */}
      <circle cx={center} cy={center} r="6" fill="#1a1a3a" stroke={kpColor} strokeWidth="1.5" />
      
      {/* Needle */}
      <line 
        x1={center} 
        y1={center} 
        x2={needleEnd.x} 
        y2={needleEnd.y} 
        stroke={kpColor} 
        strokeWidth="2"
        strokeLinecap="round"
      />
      
      {/* Sun icon in center */}
      <circle cx={center} cy={center} r="3" fill={kpColor} />
    </svg>
  );
};

// =============================================================================
// MAIN CONTEXT TILES COMPONENT
// =============================================================================
const ContextTiles = ({ 
  selectedDate, 
  extendedDateInfo,
  location, 
  cosmicData,
  onOpenDatePicker, 
  onOpenLocationPicker,
  onSetCurrentTime,
  onSetCurrentLocation,
  onOpenMoonPanel,
  onOpenSunPanel,
  onOpenPhasePanel
}) => {
  // Theme support
  const { isDark } = useTheme();
  
  // Responsive hook for device-aware UI
  const { isMobile, isTablet, isPhone, width, isShortViewport } = useResponsive();
  
  // Theme-aware background colors - using CSS variables for theme support
  const bgPrimary = 'var(--tod-bg-tertiary)';
  const bgCard = 'var(--tod-bg-overlay)';
  const textPrimary = 'var(--tod-text-primary)';
  
  // Format time display using meta standard
  const formatTime = () => {
    // Always show actual time, even if no specific date selected
    const dateToShow = selectedDate || new Date();
    return REAL_TIME_DISPLAY.formatTime(dateToShow);
  };
  
  // Format date with year using meta standard
  // Handles BCE dates using extendedDateInfo with robust fallbacks
  const formatDateWithYear = () => {
    // Check if this is a BCE date - use multiple signals for robustness
    const isBCE = extendedDateInfo?.isBCE || 
                  extendedDateInfo?.astronomicalYear < 0 ||
                  cosmicData?.is_bce ||
                  cosmicData?.astronomical_year < 0;
    
    if (isBCE) {
      // Get astronomical year from available sources
      const astroYear = extendedDateInfo?.astronomicalYear ?? 
                        cosmicData?.astronomical_year ?? 
                        null;
      
      if (astroYear != null) {
        // Get month and day from the date object (proxy year doesn't affect month/day)
        const dateToShow = selectedDate || new Date();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateToShow.getMonth()];
        const day = dateToShow.getDate();
        // Convert astronomical year to display year (astronomical -2499 = 2500 BCE)
        const displayYear = Math.abs(astroYear) + (astroYear <= 0 ? 1 : 0);
        return `${month} ${day}, ${displayYear} BCE`;
      }
      
      // Last resort: try parsing from isoDate
      if (extendedDateInfo?.isoDate) {
        const match = extendedDateInfo.isoDate.match(/^-?(\d+)-(\d{2})-(\d{2})/);
        if (match) {
          const year = parseInt(match[1], 10);
          const monthNum = parseInt(match[2], 10);
          const day = parseInt(match[3], 10);
          const displayYear = year + 1; // -2499 -> 2500 BCE
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return `${monthNames[monthNum - 1]} ${day}, ${displayYear} BCE`;
        }
      }
    }
    
    // Standard CE date
    const dateToShow = selectedDate || new Date();
    return REAL_TIME_DISPLAY.formatDateWithYear(dateToShow);
  };
  
  // Format city (primary display)
  const formatCity = () => {
    return location?.city || 'Unknown';
  };
  
  // Format state/country codes (secondary display)
  const formatStateCountryCodes = () => {
    return REAL_PLACE_DISPLAY.formatStateCountryCodes(location);
  };
  
  // Extract lunar data from cosmicData
  const lunarData = useMemo(() => {
    const moon = cosmicData?.positions?.moon;
    if (!moon) return null;
    
    // illumination from API is already a percentage (0-100 range)
    // Ensure it's clamped to valid range
    const rawIllumination = moon.illumination || 0;
    const illumination = Math.min(100, Math.max(0, rawIllumination));
    
    return {
      phase: moon.phase_name || 'Unknown',
      illumination: illumination, // Already a percentage, no multiplication needed
      phaseAngle: moon.phase_angle || 0,
      sign: moon.zodiac_sign || 'Unknown',
      cihPhase: getLunarCihPhase(moon.phase_name)
    };
  }, [cosmicData]);
  
  // Extract solar/space weather data
  const solarData = useMemo(() => {
    const spaceWeather = cosmicData?.spacio_atmospheric?.space_weather;
    const solar = spaceWeather?.solar_cycle || {};
    const geo = spaceWeather?.geomagnetic || {};
    
    const kp = geo.kp_index || geo.estimated_kp || 2;
    
    // Prefer backend-provided NOAA descriptor; fall back to KP-derived ternary if absent
    let kpDescription = geo.kp_description;
    if (!kpDescription) {
      if (kp >= 7) kpDescription = 'Major Storm';
      else if (kp >= 5) kpDescription = 'Minor Storm';
      else if (kp >= 4) kpDescription = 'Active';
      else if (kp >= 3) kpDescription = 'Unsettled';
      else kpDescription = 'Quiet';
    }
    
    return {
      kpIndex: kp,
      kpDescription,
      activityLevel: solar.activity_level || 'moderate',  // Solar cycle activity (multi-year context)
      solarCycle: solar.cycle_number || 25,
      cyclePhase: solar.phase || 'ascending',
      auroraNote: geo.aurora_visibility || null
    };
  }, [cosmicData]);

  // Extract phase emphasis data
  const phaseData = useMemo(() => {
    const cih = cosmicData?.cih_intelligence;
    if (!cih) return null;
    
    // Use phase_percentages if available (nested model), fallback to calculating from scores
    let percentages = cih.phase_percentages;
    if (!percentages) {
      // Fallback: calculate percentages from raw scores
      const scores = cih.phase_scores || { NOURISH: 2, ACTIVATE: 2, FULFILL: 2, INTEGRATE: 2 };
      const total = Object.values(scores).reduce((a, b) => a + b, 0) || 1;
      percentages = {
        NOURISH: Math.round((scores.NOURISH / total) * 100),
        ACTIVATE: Math.round((scores.ACTIVATE / total) * 100),
        FULFILL: Math.round((scores.FULFILL / total) * 100),
        INTEGRATE: Math.round((scores.INTEGRATE / total) * 100)
      };
    }
    
    return {
      dominant: cih.dominant_phase || 'NOURISH',
      clarity: cih.convergence_clarity || 'moderate',
      percentages: percentages,
      // Keep scores for backward compatibility if needed
      scores: cih.phase_scores || { NOURISH: 2, ACTIVATE: 2, FULFILL: 2, INTEGRATE: 2 }
    };
  }, [cosmicData]);

  // Theme-aware styles with consistent gold borders matching title container
  const tileBaseClass = `backdrop-blur-md border transition-all`;
  const tileBaseBg = isDark ? 'bg-[#030408]/95' : 'bg-white/95';
  const smallTileClass = `${tileBaseClass} ${tileBaseBg} border-[var(--tod-brass)]/30 hover:border-[var(--tod-brass)]/50 px-2 py-1.5 sm:px-3 sm:py-2`;
  const quickButtonClass = `${tileBaseClass} ${tileBaseBg} border-[var(--tod-brass)]/30 hover:border-[var(--tod-brass)]/50 hover:bg-tod-gold/10 px-2 py-1`;
  const largeTileClass = `${tileBaseClass} ${tileBaseBg} border-[var(--tod-brass)]/30 p-3`;
  const phaseTileClass = `${tileBaseClass} ${tileBaseBg} border-[var(--tod-brass)]/30 p-2`; // For phase emphasis tile (2:1 ratio)

  return (
    <div 
      className="context-tiles flex flex-col gap-2"
      data-testid="context-tiles"
      style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
    >
      {/* ROW 1: Time and Place tiles (side by side) */}
      <div className="flex gap-2">
        {/* Time Tile */}
        <button
          onClick={onOpenDatePicker}
          className={`${smallTileClass} flex-1 group text-left`}
          data-testid="context-tile-time"
          aria-label={`Current time: ${formatTime()}. Click to change.`}
        >
          <div className="flex items-center gap-1.5">
            <Clock size={14} className="text-tod-gold group-hover:text-tod-brass flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] text-tod-brass uppercase tracking-wider leading-none font-semibold">Time</div>
              <div className="text-sm text-tod-text-primary font-bold truncate leading-tight">
                {formatTime()}
              </div>
              <div className="text-[10px] text-tod-text-secondary leading-none truncate">
                {formatDateWithYear()}
              </div>
            </div>
          </div>
        </button>
        
        {/* Place Tile */}
        <button
          onClick={onOpenLocationPicker}
          className={`${smallTileClass} flex-1 group text-left`}
          data-testid="context-tile-place"
          aria-label={`Current location: ${formatCity()}. Click to change.`}
        >
          <div className="flex items-center gap-1.5">
            <MapPin size={14} className="text-tod-gold group-hover:text-tod-brass flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] text-tod-brass uppercase tracking-wider leading-none font-semibold">Place</div>
              <div className="text-sm text-tod-text-primary font-bold truncate leading-tight">
                {formatCity()}
              </div>
              <div className="text-[10px] text-tod-text-secondary leading-none truncate">
                {formatStateCountryCodes()}
              </div>
            </div>
          </div>
        </button>
      </div>
      
      {/* ROW 2: Quick action buttons (side by side) */}
      <div className="flex gap-2">
        {/* Current Time Button */}
        <button
          onClick={onSetCurrentTime}
          className={`${quickButtonClass} flex-1 group`}
          data-testid="quick-set-current-time"
          aria-label="Set to current time"
        >
          <div className="flex items-center justify-center gap-1.5">
            <RefreshCw size={12} className="text-tod-gold/70 group-hover:text-tod-gold" />
            <span className="text-[10px] text-tod-text-secondary group-hover:text-tod-text-primary font-medium uppercase tracking-wide">
              Now
            </span>
          </div>
        </button>
        
        {/* Current Location Button */}
        <button
          onClick={onSetCurrentLocation}
          className={`${quickButtonClass} flex-1 group`}
          data-testid="quick-set-current-location"
          aria-label="Set to current location"
        >
          <div className="flex items-center justify-center gap-1.5">
            <Navigation size={12} className="text-tod-gold/70 group-hover:text-tod-gold" />
            <span className="text-[10px] text-tod-text-secondary group-hover:text-tod-text-primary font-medium uppercase tracking-wide">
              Here
            </span>
          </div>
        </button>
      </div>
      
      {/* ROW 3: Moon Tile (Large - 4:1 aspect) */}
      <div 
        className={`${largeTileClass} group cursor-pointer hover:border-tod-silver`}
        data-testid="context-tile-moon"
        onClick={onOpenMoonPanel}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onOpenMoonPanel?.()}
        aria-label={`Moon: ${lunarData?.phase || 'Unknown Phase'} in ${lunarData?.sign || 'Unknown'}. Click to view details.`}
      >
        <div className="flex items-center gap-3">
          {/* Lunar Phase Visual */}
          <div className="flex-shrink-0">
            <LunarPhaseVisual 
              illumination={lunarData?.illumination || 50} 
              phaseAngle={lunarData?.phaseAngle || 180}
              size={48}
            />
          </div>
          
          {/* Moon Data - Compact Layout */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Moon size={11} className="text-[#c0c0a8] flex-shrink-0" />
              <span className="text-[10px] text-tod-brass uppercase tracking-wider font-bold">Luna</span>
              {lunarData?.cihPhase && (
                <span 
                  className="text-[10px] px-1 py-0.5 rounded uppercase font-bold ml-auto"
                  style={{ 
                    backgroundColor: getCihPhaseColor(lunarData.cihPhase) + '30',
                    color: getCihPhaseColor(lunarData.cihPhase)
                  }}
                >
                  {lunarData.cihPhase}
                </span>
              )}
            </div>
            
            {/* Phase + Sign on one line */}
            <div className="text-sm text-tod-text-primary font-bold leading-tight truncate">
              {lunarData?.phase || 'Unknown'} 
              <span className="text-tod-text-secondary font-normal text-xs ml-1">
                in {lunarData?.sign}
              </span>
            </div>
            
            {/* Illumination - no multiplication, already a percentage */}
            <div className="text-[10px] text-tod-text-secondary mt-0.5">
              {(lunarData?.illumination || 0).toFixed(0)}% illuminated
            </div>
          </div>
        </div>
      </div>
      
      {/* ROW 4: Sun/Solar Weather Tile (Large - 4:1 aspect) */}
      <div 
        className={`${largeTileClass} group cursor-pointer hover:border-tod-gold`}
        data-testid="context-tile-solar"
        onClick={onOpenSunPanel}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onOpenSunPanel?.()}
        aria-label={`Sun: KP ${solarData?.kpIndex?.toFixed(1) || '2.0'} ${solarData?.kpDescription || 'Quiet'}. Solar activity ${solarData?.activityLevel || 'moderate'}. Click to view details.`}
      >
        <div className="flex items-center gap-3">
          {/* Solar Weather Visual */}
          <div className="flex-shrink-0">
            <SolarWeatherVisual 
              kpIndex={solarData?.kpIndex || 2}
              activityLevel={solarData?.activityLevel}
              size={48}
            />
          </div>
          
          {/* Solar Data - Compact Layout */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Sun size={11} className="text-tod-gold flex-shrink-0" />
              <span className="text-[10px] text-tod-brass uppercase tracking-wider font-bold">Sol</span>
              <Zap size={10} className={`ml-auto flex-shrink-0 ${solarData?.kpIndex > 4 ? 'text-yellow-400' : 'text-tod-text-secondary'}`} />
            </div>
            
            {/* KP + KP descriptor on one line */}
            <div className="text-sm text-tod-text-primary font-bold leading-tight">
              KP {solarData?.kpIndex?.toFixed(1) || '2.0'}
              <span
                className="text-tod-text-secondary font-normal text-xs ml-1.5"
                data-testid="solar-kp-descriptor"
              >
                {solarData?.kpDescription || 'Quiet'}
              </span>
            </div>
            
            {/* Solar activity (cycle-level, multi-year context) */}
            <div className="text-[10px] text-tod-text-secondary mt-0.5 truncate" data-testid="solar-activity-label">
              <span className="text-tod-brass uppercase tracking-wider font-semibold">Solar activity:</span>{' '}
              <span className="capitalize">{(solarData?.activityLevel || 'moderate').replace('_', ' ')}</span>
            </div>
            
            {/* Cycle info on its own line */}
            <div className="text-[10px] text-tod-text-secondary mt-0.5 truncate">
              Cycle {solarData?.solarCycle} · {solarData?.cyclePhase}
            </div>
          </div>
        </div>
      </div>

      {/* ROW 5: Phase Emphasis Tile (Wide - 2:1 aspect ratio) */}
      {phaseData && (
        <button
          className={`${phaseTileClass} group cursor-pointer hover:border-tod-brass aspect-[2/1]`}
          data-testid="context-tile-phase"
          onClick={() => onOpenPhasePanel?.(phaseData.dominant)}
          aria-label={`Phase Emphasis: ${phaseData.dominant}. Click to view details.`}
        >
          <div className="flex flex-col h-full justify-between">
            {/* Header with dominant phase label */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5">
                <Layers size={11} className="text-tod-brass flex-shrink-0" />
                <span className="text-[10px] text-tod-brass uppercase tracking-wider font-bold">Phase Emphasis</span>
              </div>
              {/* Dominant phase badge (was convergence) */}
              <span 
                className="text-sm px-2 py-0.5 rounded uppercase font-bold"
                style={{ 
                  backgroundColor: getCihPhaseColor(phaseData.dominant) + '30',
                  color: getCihPhaseColor(phaseData.dominant)
                }}
              >
                {phaseData.dominant}
              </span>
            </div>
            
            {/* Phase Hierarchy Spectrum */}
            <div className="flex-1 flex flex-col justify-center">
              {/* Phase Bar - using percentages from nested model */}
              <div className="h-2.5 rounded-full overflow-hidden flex bg-tod-bg-tertiary">
                {['NOURISH', 'ACTIVATE', 'FULFILL', 'INTEGRATE'].map(phase => {
                  // Use pre-calculated percentages (guarded with fallback)
                  const percentage = phaseData.percentages?.[phase] || 25;
                  const color = getCihPhaseColor(phase);
                  return (
                    <div 
                      key={phase} 
                      style={{ width: `${percentage}%`, backgroundColor: color }}
                      className={phase === phaseData.dominant ? 'relative' : ''}
                      title={`${phase}: ${percentage}%`}
                    >
                      {phase === phaseData.dominant && (
                        <div className="absolute inset-0 animate-pulse bg-white/20" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

export default ContextTiles;
